// Opens the per-tenant ingestion WebSocket whenever the user is signed
// in, parses every job.update / job.failed frame, and pushes a toast.
//
// Phase 1 mounted the WS scaffold at /api/v1/ws/processing; Phase 2
// wired real per-stage NOTIFY → frames. Auth is via ?token=... because
// browsers can't set Authorization headers on a WS upgrade. The token
// itself is a short-lived (15 min) access JWT, so leakage in URL
// history is bounded.

import { useEffect, useRef } from "react";

import { useAuthStore } from "../lib/store";
import { pushToast } from "../components/Toast";

interface JobFrame {
  type: string;
  document_id?: number;
  stage?: string;
  progress_pct?: number;
  status?: string;
  ts?: string;
  error_message?: string;
  tenant_id?: number;
  user_id?: number;
  channel?: string;
}

const STAGE_LABEL: Record<string, string> = {
  queued: "queued",
  parse: "parsing",
  redact: "redacting PII",
  classify: "classifying",
  metadata: "extracting metadata",
  index: "indexing",
  embed: "embedding",
  complete: "complete",
  failed: "failed",
};

function describe(frame: JobFrame): string {
  const doc = frame.document_id ? `Document #${frame.document_id}` : "Document";
  if (frame.type === "job.failed" || frame.stage === "failed") {
    return `${doc} ingestion failed${frame.error_message ? `: ${frame.error_message}` : ""}`;
  }
  const stage = frame.stage ? STAGE_LABEL[frame.stage] ?? frame.stage : "update";
  if (frame.stage === "complete") {
    return `${doc} ingestion complete`;
  }
  return `${doc} — ${stage} (${frame.progress_pct ?? 0}%)`;
}

export function useProcessingSocket(): void {
  const token = useAuthStore((s) => s.accessToken);
  const ws = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!token) return;
    if (typeof window === "undefined" || typeof WebSocket === "undefined") return;

    // Same-origin WS — Vite proxies /api/v1/ws/* to the backend in
    // dev; nginx does the same in prod. So `ws://<host>:<port>/api/v1/...`
    // works in both.
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${protocol}//${window.location.host}/api/v1/ws/processing?token=${encodeURIComponent(token)}`;

    let cancelled = false;
    let socket: WebSocket;
    try {
      socket = new WebSocket(url);
    } catch {
      // Older Safari + some test envs throw synchronously on a bad
      // URL. Just bail — the UI still works without live updates.
      return;
    }
    ws.current = socket;

    socket.onmessage = (ev) => {
      if (cancelled) return;
      try {
        const frame: JobFrame = JSON.parse(typeof ev.data === "string" ? ev.data : "");
        if (frame.type === "hello") return; // first frame; informational
        if (frame.type === "ping") return; // heartbeat
        const msg = describe(frame);
        const kind =
          frame.type === "job.failed" || frame.stage === "failed"
            ? "error"
            : frame.stage === "complete"
              ? "success"
              : "info";
        pushToast(kind, msg);
      } catch {
        // Non-JSON or unknown frame — ignore.
      }
    };

    return () => {
      cancelled = true;
      try {
        socket.close();
      } catch {
        /* nothing */
      }
      ws.current = null;
    };
  }, [token]);
}
